import type { FieldPath, FieldValues } from 'react-hook-form'
import type { Style } from 'inlines'
import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import type {
  ComponentProps,
  FocusEventHandler,
  KeyboardEventHandler,
  MutableRefObject,
  ReactNode,
} from 'react'
import { TextInput } from '../../input/text/TextInput'
import { FormField } from '../FormField'
import { useFieldRequired } from '../formSchemaContext'

export type NumberFieldProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>
  label?: string
  labelStyle?: Style
  required?: boolean
  placeholder?: string
  disabled?: boolean
  icon?: IconDefinition
  endAdornment?: ReactNode
  style?: Style
  containerStyle?: Style
  inputRef?: MutableRefObject<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  inputMode?: ComponentProps<'input'>['inputMode']
  min?: number
  max?: number
}

export const NumberField = <T extends FieldValues = FieldValues>({
  name,
  inputRef,
  min,
  max,
  required,
  ...inputProps
}: NumberFieldProps<T>) => {
  const fieldRequired = useFieldRequired(name)
  const isRequired = required ?? fieldRequired

  return (
    <FormField<T>
      name={name}
      render={({ value, onChange, onBlur, errorMessage }) => (
        <TextInput
          {...inputProps}
          required={isRequired}
          inputType="number"
          min={min}
          max={max}
          text={
            typeof value === 'number' || typeof value === 'string'
              ? String(value)
              : ''
          }
          setText={(raw) => {
            const parsed = raw === '' ? undefined : Number(raw)
            onChange(parsed)
          }}
          onBlur={onBlur}
          errorMessage={errorMessage}
          ref={inputRef}
        />
      )}
    />
  )
}
